import React from 'react';
import { Box, Card, CardContent, Stack, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ChevronRight, HeartPulse } from 'lucide-react';
import type { IWellnessInviteCardProps } from '../../../interfaces';
import { formatShortDate, isActionableInvite } from '../../functions';
import { WellnessTypePill } from './WellnessTypePill';

/** Clickable invite card rendered in the wellness grid. Built with MUI Card. */
export const WellnessInviteCard = ({ invite, onClick }: IWellnessInviteCardProps) => {
  const jobTitle = invite.jobTitle ?? 'Wellness Screening';
  const sentDate = invite.sentAt ? formatShortDate(invite.sentAt) : null;
  const actionable = isActionableInvite(invite);

  return (
    <Card
      component="article"
      onClick={onClick}
      onKeyDown={(event: React.KeyboardEvent) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onClick();
        }
      }}
      tabIndex={0}
      role="button"
      aria-label={`${jobTitle} wellness screening`}
      sx={{
        cursor: 'pointer',
        '&:focus-visible': {
          outline: 'none',
          boxShadow: (theme) => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.5)}`,
        },
      }}
    >
      <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, p: 2.5, '&:last-child': { pb: 2.5 } }}>
        {/* Header row: icon + assessment count */}
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box
            sx={{
              width: 40,
              height: 40,
              borderRadius: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              color: 'primary.main',
              flexShrink: 0,
            }}
          >
            <HeartPulse size={20} aria-hidden="true" />
          </Box>
          <Typography variant="caption" color="text.secondary">
            {invite.types.length} assessment{invite.types.length !== 1 ? 's' : ''}
          </Typography>
        </Stack>

        {/* Title + org + date */}
        <Box>
          <Typography variant="h4">{jobTitle}</Typography>
          {invite.organizationName && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              {invite.organizationName}
            </Typography>
          )}
          {sentDate && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
              Sent {sentDate}
            </Typography>
          )}
        </Box>

        {/* Assessment type pills */}
        <Box sx={{ pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack spacing={1}>
            {invite.types.map((item) => (
              <WellnessTypePill key={item.type} item={item} />
            ))}
          </Stack>
        </Box>

        {/* CTA link */}
        {actionable && (
          <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'primary.main' }}>
            <Typography variant="caption" fontWeight={600}>
              Continue
            </Typography>
            <ChevronRight size={14} aria-hidden="true" />
          </Stack>
        )}
      </CardContent>
    </Card>
  );
};
